import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2, Users, Shield, ChevronRight, ChevronDown, Plus, Edit, Trash2,
  Check, X, Search, Filter, MoreVertical, UserPlus, Building, Globe,
  Briefcase, Plane, Wrench, Truck, GraduationCap, MapPin, Crown, Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

// Organization type icons
const orgTypeIcons = {
  regulatory_authority: Globe,
  holding_company: Building2,
  operator: Plane,
  camo: Shield,
  mro: Wrench,
  ground_handler: Truck,
  training_org: GraduationCap,
  division: Briefcase,
  department: Users,
  base: MapPin,
};

// Organization type labels
const orgTypeLabels = {
  regulatory_authority: 'Regulatory Authority',
  holding_company: 'Holding Company',
  operator: 'Operator (AOC)',
  camo: 'CAMO',
  mro: 'MRO',
  ground_handler: 'Ground Handler',
  training_org: 'Training Organization',
  division: 'Division',
  department: 'Department',
  base: 'Base/Station',
};

// Aviation-specific codes by organization type
const orgTypeCodes = {
  division: [
    { code: 'FLT-OPS', label: 'Flight Operations', label_ar: 'عمليات الطيران' },
    { code: 'MNT', label: 'Maintenance', label_ar: 'الصيانة' },
    { code: 'GND-OPS', label: 'Ground Operations', label_ar: 'العمليات الأرضية' },
    { code: 'CABIN', label: 'Cabin Services', label_ar: 'خدمات المقصورة' },
    { code: 'CARGO', label: 'Cargo Operations', label_ar: 'عمليات الشحن' },
    { code: 'SQ', label: 'Safety & Quality', label_ar: 'السلامة والجودة' },
    { code: 'HR', label: 'Human Resources', label_ar: 'الموارد البشرية' },
    { code: 'FINANCE', label: 'Finance', label_ar: 'المالية' },
    { code: 'IT', label: 'Information Technology', label_ar: 'تقنية المعلومات' },
  ],
  department: [
    // Flight Operations Departments
    { code: 'CAPT', label: 'Captains / Commanders', label_ar: 'القباطنة', category: 'Cockpit Crew' },
    { code: 'FO', label: 'First Officers', label_ar: 'مساعدو الطيار', category: 'Cockpit Crew' },
    { code: 'SO', label: 'Second Officers', label_ar: 'الضباط الثانيون', category: 'Cockpit Crew' },
    { code: 'TRE', label: 'Type Rating Examiners', label_ar: 'فاحصو تصنيف النوع', category: 'Cockpit Crew' },
    { code: 'TRI', label: 'Type Rating Instructors', label_ar: 'مدربو تصنيف النوع', category: 'Cockpit Crew' },
    { code: 'CC-MGR', label: 'Cabin Crew Managers', label_ar: 'مديرو طاقم المقصورة', category: 'Cabin Crew' },
    { code: 'PURSER', label: 'Pursers / Cabin Supervisors', label_ar: 'مشرفو المقصورة', category: 'Cabin Crew' },
    { code: 'FA', label: 'Flight Attendants', label_ar: 'مضيفو الطيران', category: 'Cabin Crew' },
    { code: 'DSP', label: 'Flight Dispatch', label_ar: 'إرسال الرحلات', category: 'Operations' },
    { code: 'OCC', label: 'Operations Control Center', label_ar: 'مركز التحكم بالعمليات', category: 'Operations' },
    { code: 'CREW-PLAN', label: 'Crew Planning', label_ar: 'تخطيط الطاقم', category: 'Operations' },
    // Maintenance Departments
    { code: 'LINE-MNT', label: 'Line Maintenance', label_ar: 'صيانة الخط', category: 'Maintenance' },
    { code: 'HEAVY-MNT', label: 'Heavy Maintenance', label_ar: 'الصيانة الثقيلة', category: 'Maintenance' },
    { code: 'AVIONICS', label: 'Avionics', label_ar: 'إلكترونيات الطيران', category: 'Maintenance' },
    { code: 'ENGINE', label: 'Engine Shop', label_ar: 'ورشة المحركات', category: 'Maintenance' },
    { code: 'STRUCT', label: 'Structures', label_ar: 'الهياكل', category: 'Maintenance' },
    { code: 'NDT', label: 'NDT / Inspection', label_ar: 'الفحص غير المدمر', category: 'Maintenance' },
    { code: 'MCC', label: 'Maintenance Control', label_ar: 'التحكم بالصيانة', category: 'Maintenance' },
    { code: 'PLANNING', label: 'Maintenance Planning', label_ar: 'تخطيط الصيانة', category: 'Maintenance' },
    // Ground Operations Departments  
    { code: 'RAMP', label: 'Ramp Services', label_ar: 'خدمات المنصة', category: 'Ground' },
    { code: 'PAX', label: 'Passenger Services', label_ar: 'خدمات الركاب', category: 'Ground' },
    { code: 'BAGGAGE', label: 'Baggage Handling', label_ar: 'مناولة الأمتعة', category: 'Ground' },
    { code: 'LOAD', label: 'Load Control', label_ar: 'التحكم بالحمولة', category: 'Ground' },
    { code: 'FUEL', label: 'Fuel Services', label_ar: 'خدمات الوقود', category: 'Ground' },
    // Safety & Quality Departments
    { code: 'SAFETY', label: 'Safety Department', label_ar: 'قسم السلامة', category: 'Safety' },
    { code: 'QA', label: 'Quality Assurance', label_ar: 'ضمان الجودة', category: 'Safety' },
    { code: 'COMP', label: 'Compliance Monitoring', label_ar: 'مراقبة الامتثال', category: 'Safety' },
    { code: 'SMS', label: 'SMS Office', label_ar: 'مكتب إدارة السلامة', category: 'Safety' },
    // Training Departments
    { code: 'FLT-TRG', label: 'Flight Training', label_ar: 'التدريب على الطيران', category: 'Training' },
    { code: 'CABIN-TRG', label: 'Cabin Crew Training', label_ar: 'تدريب طاقم المقصورة', category: 'Training' },
    { code: 'MNT-TRG', label: 'Maintenance Training', label_ar: 'تدريب الصيانة', category: 'Training' },
    { code: 'GND-TRG', label: 'Ground Staff Training', label_ar: 'تدريب الطاقم الأرضي', category: 'Training' },
    { code: 'SIM', label: 'Simulator Operations', label_ar: 'عمليات المحاكاة', category: 'Training' },
  ],
  base: [
    // IATA codes for major Saudi airports
    { code: 'JED', label: 'Jeddah (King Abdulaziz)', label_ar: 'جدة' },
    { code: 'RUH', label: 'Riyadh (King Khalid)', label_ar: 'الرياض' },
    { code: 'DMM', label: 'Dammam (King Fahd)', label_ar: 'الدمام' },
    { code: 'MED', label: 'Madinah', label_ar: 'المدينة المنورة' },
    { code: 'AHB', label: 'Abha', label_ar: 'أبها' },
    { code: 'TIF', label: 'Taif', label_ar: 'الطائف' },
    { code: 'GIZ', label: 'Jazan', label_ar: 'جيزان' },
    { code: 'TUU', label: 'Tabuk', label_ar: 'تبوك' },
    // Common international hubs
    { code: 'DXB', label: 'Dubai', label_ar: 'دبي' },
    { code: 'DOH', label: 'Doha', label_ar: 'الدوحة' },
    { code: 'AUH', label: 'Abu Dhabi', label_ar: 'أبوظبي' },
    { code: 'CAI', label: 'Cairo', label_ar: 'القاهرة' },
    { code: 'LHR', label: 'London Heathrow', label_ar: 'لندن هيثرو' },
    { code: 'CDG', label: 'Paris CDG', label_ar: 'باريس' },
    { code: 'FRA', label: 'Frankfurt', label_ar: 'فرانكفورت' },
  ],
  regulatory_authority: [
    { code: 'GACA', label: 'Saudi GACA', label_ar: 'الهيئة العامة للطيران المدني' },
    { code: 'EASA', label: 'European EASA', label_ar: 'وكالة سلامة الطيران الأوروبية' },
    { code: 'FAA', label: 'US FAA', label_ar: 'إدارة الطيران الفيدرالية' },
    { code: 'ICAO', label: 'ICAO', label_ar: 'منظمة الطيران المدني الدولي' },
    { code: 'GCAA', label: 'UAE GCAA', label_ar: 'الهيئة العامة للطيران المدني الإماراتية' },
    { code: 'QCAA', label: 'Qatar CAA', label_ar: 'هيئة الطيران المدني القطرية' },
  ],
};

// Role labels
const roleLabels = {
  owner: 'Owner',
  manager: 'Manager',
  analyst: 'Analyst',
  reporter: 'Reporter',
  viewer: 'Viewer',
  contractor: 'Contractor',
  regulator: 'Regulator',
};

// Role colors
const roleColors = {
  owner: 'bg-purple-500',
  manager: 'bg-blue-500',
  analyst: 'bg-green-500',
  reporter: 'bg-yellow-500',
  viewer: 'bg-slate-500',
  contractor: 'bg-orange-500',
  regulator: 'bg-red-500',
};

const OrganizationManagementPage = () => {
  const { api } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [members, setMembers] = useState([]);
  const [userContext, setUserContext] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('hierarchy');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [expandedOrgs, setExpandedOrgs] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [showCreateOrgDialog, setShowCreateOrgDialog] = useState(false);
  const [showEditOrgDialog, setShowEditOrgDialog] = useState(false);
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [showMembersDialog, setShowMembersDialog] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  
  // Form states
  const [orgForm, setOrgForm] = useState({
    name: '',
    name_ar: '',
    code: '',
    org_type: 'department',
    description: '',
    parent_id: null,
    regulatory_authority: '',
    aoc_number: '',
  });
  
  const [memberForm, setMemberForm] = useState({
    user_id: '',
    role: 'viewer',
    is_primary: false,
    can_access_children: false,
    can_access_parent: false,
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!api) return;
    
    try {
      setLoading(true);
      const [orgsRes, contextRes, usersRes] = await Promise.all([
        api.get('/multiorg/organizations'),
        api.get('/multiorg/context/me'),
        api.get('/users').catch(() => ({ data: [] })),
      ]);
      setOrganizations(orgsRes.data);
      setUserContext(contextRes.data);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load organization data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Build organization tree
  const buildOrgTree = useCallback(() => {
    const orgMap = {};
    const roots = [];
    
    organizations.forEach(org => {
      orgMap[org.id] = { ...org, children: [] };
    });
    
    organizations.forEach(org => {
      if (org.parent_id && orgMap[org.parent_id]) {
        orgMap[org.parent_id].children.push(orgMap[org.id]);
      } else if (!org.parent_id) {
        roots.push(orgMap[org.id]);
      }
    });
    
    return roots;
  }, [organizations]);

  // Toggle organization expand
  const toggleExpand = (orgId) => {
    setExpandedOrgs(prev => ({
      ...prev,
      [orgId]: !prev[orgId]
    }));
  };

  // Handle create organization
  const handleCreateOrg = async () => {
    try {
      await api.post('/multiorg/organizations', orgForm);
      toast.success('Organization created successfully');
      setShowCreateOrgDialog(false);
      setOrgForm({
        name: '',
        name_ar: '',
        code: '',
        org_type: 'department',
        description: '',
        parent_id: null,
        regulatory_authority: '',
        aoc_number: '',
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create organization');
    }
  };

  // Handle update organization
  const handleUpdateOrg = async () => {
    try {
      await api.put(`/multiorg/organizations/${editingOrg.id}`, orgForm);
      toast.success('Organization updated successfully');
      setShowEditOrgDialog(false);
      setEditingOrg(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update organization');
    }
  };

  // Handle delete organization
  const handleDeleteOrg = async (orgId) => {
    if (!window.confirm('Are you sure you want to deactivate this organization?')) return;
    
    try {
      await api.delete(`/multiorg/organizations/${orgId}`);
      toast.success('Organization deactivated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate organization');
    }
  };

  // Fetch organization members
  const fetchOrgMembers = async (orgId) => {
    try {
      const res = await api.get(`/multiorg/organizations/${orgId}/members`);
      setMembers(res.data);
      setSelectedOrg(organizations.find(o => o.id === orgId));
      setShowMembersDialog(true);
    } catch (error) {
      toast.error('Failed to fetch members');
    }
  };

  // Handle add member
  const handleAddMember = async () => {
    try {
      await api.post('/multiorg/memberships', {
        ...memberForm,
        organization_id: selectedOrg.id,
      });
      toast.success('Member added successfully');
      setShowAddMemberDialog(false);
      setMemberForm({
        user_id: '',
        role: 'viewer',
        is_primary: false,
        can_access_children: false,
        can_access_parent: false,
      });
      fetchOrgMembers(selectedOrg.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  // Handle remove member
  const handleRemoveMember = async (membershipId) => {
    if (!window.confirm('Remove this member from the organization?')) return;
    
    try {
      await api.delete(`/multiorg/memberships/${membershipId}`);
      toast.success('Member removed');
      fetchOrgMembers(selectedOrg.id);
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  // Render organization tree item
  const renderOrgTreeItem = (org, level = 0) => {
    const hasChildren = org.children && org.children.length > 0;
    const isExpanded = expandedOrgs[org.id];
    const Icon = orgTypeIcons[org.org_type] || Building2;
    
    return (
      <div key={org.id}>
        <div 
          className={`flex items-center gap-2 p-2 rounded-lg hover:bg-slate-800/50 cursor-pointer transition-colors ${
            selectedOrg?.id === org.id ? 'bg-slate-800' : ''
          }`}
          style={{ paddingLeft: `${level * 24 + 8}px` }}
        >
          {hasChildren ? (
            <button 
              onClick={() => toggleExpand(org.id)}
              className="p-1 hover:bg-slate-700 rounded"
            >
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          ) : (
            <div className="w-6" />
          )}
          
          <Icon className="w-5 h-5 text-sky-400" />
          
          <div className="flex-1" onClick={() => setSelectedOrg(org)}>
            <span className="font-medium">{org.name}</span>
            <span className="text-slate-400 text-sm ml-2">({org.code})</span>
          </div>
          
          <Badge variant="outline" className="text-xs">
            {orgTypeLabels[org.org_type] || org.org_type}
          </Badge>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => fetchOrgMembers(org.id)}>
                <Users className="w-4 h-4 mr-2" />
                View Members
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                setEditingOrg(org);
                setOrgForm({
                  name: org.name,
                  name_ar: org.name_ar || '',
                  code: org.code,
                  org_type: org.org_type,
                  description: org.description || '',
                  parent_id: org.parent_id,
                  regulatory_authority: org.regulatory_authority || '',
                  aoc_number: org.aoc_number || '',
                });
                setShowEditOrgDialog(true);
              }}>
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                setOrgForm(prev => ({ ...prev, parent_id: org.id }));
                setShowCreateOrgDialog(true);
              }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Child
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => handleDeleteOrg(org.id)}
                className="text-red-400"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Deactivate
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {hasChildren && isExpanded && (
          <div>
            {org.children.map(child => renderOrgTreeItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // Filter organizations
  const filteredOrgs = organizations.filter(org => 
    org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    org.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="org-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Organization Management</h1>
          <p className="text-slate-400">Manage organizational hierarchy, members, and access control</p>
        </div>
        <Button onClick={() => setShowCreateOrgDialog(true)} data-testid="create-org-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Organization
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Organizations</p>
                <p className="text-2xl font-bold">{organizations.length}</p>
              </div>
              <Building2 className="w-8 h-8 text-sky-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">My Memberships</p>
                <p className="text-2xl font-bold">{userContext?.memberships?.length || 0}</p>
              </div>
              <Users className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">My Permissions</p>
                <p className="text-2xl font-bold">{userContext?.permissions?.length || 0}</p>
              </div>
              <Shield className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Accessible Orgs</p>
                <p className="text-2xl font-bold">{userContext?.accessible_org_ids?.length || 0}</p>
              </div>
              <Eye className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800">
          <TabsTrigger value="hierarchy">Hierarchy View</TabsTrigger>
          <TabsTrigger value="list">List View</TabsTrigger>
          <TabsTrigger value="my-access">My Access</TabsTrigger>
        </TabsList>

        {/* Hierarchy View */}
        <TabsContent value="hierarchy" className="mt-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search organizations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700"
                  />
                </div>
                <Button variant="outline" onClick={() => setExpandedOrgs({})}>
                  Collapse All
                </Button>
                <Button variant="outline" onClick={() => {
                  const expanded = {};
                  organizations.forEach(o => { expanded[o.id] = true; });
                  setExpandedOrgs(expanded);
                }}>
                  Expand All
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {buildOrgTree().map(org => renderOrgTreeItem(org))}
              </div>
              {organizations.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No organizations found</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setShowCreateOrgDialog(true)}
                  >
                    Create First Organization
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* List View */}
        <TabsContent value="list" className="mt-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead>Organization</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Children</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrgs.map(org => {
                    const Icon = orgTypeIcons[org.org_type] || Building2;
                    return (
                      <TableRow key={org.id} className="border-slate-800">
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-sky-400" />
                            <div>
                              <p className="font-medium">{org.name}</p>
                              {org.name_ar && (
                                <p className="text-sm text-slate-400">{org.name_ar}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {orgTypeLabels[org.org_type]}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono">{org.code}</TableCell>
                        <TableCell>{org.level}</TableCell>
                        <TableCell>{org.children_count}</TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => fetchOrgMembers(org.id)}
                          >
                            <Users className="w-4 h-4 mr-1" />
                            Members
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* My Access */}
        <TabsContent value="my-access" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* My Memberships */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-sky-400" />
                  My Organization Memberships
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {userContext?.memberships?.map(membership => (
                    <div 
                      key={membership.id}
                      className="p-3 bg-slate-800 rounded-lg"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${roleColors[membership.role]}`} />
                          <div>
                            <p className="font-medium">
                              {membership.organization?.name || 'Unknown Org'}
                            </p>
                            <div className="text-sm text-slate-400 flex items-center flex-wrap gap-1">
                              <span>{roleLabels[membership.role]}</span>
                              {membership.is_primary && (
                                <Badge className="ml-1 bg-amber-500/20 text-amber-400">
                                  <Crown className="w-3 h-3 mr-1" />
                                  Primary
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {membership.can_access_children && (
                            <Badge variant="outline" className="text-xs">+Children</Badge>
                          )}
                          {membership.can_access_parent && (
                            <Badge variant="outline" className="text-xs">+Parent</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!userContext?.memberships || userContext.memberships.length === 0) && (
                    <p className="text-slate-400 text-center py-4">
                      No organization memberships found
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* My Permissions */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-400" />
                  My Permissions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 max-h-96 overflow-y-auto">
                  {userContext?.permissions?.map(perm => (
                    <Badge 
                      key={perm} 
                      variant="outline"
                      className={`text-xs ${
                        perm.startsWith('read:') ? 'border-green-500 text-green-400' :
                        perm.startsWith('create:') ? 'border-blue-500 text-blue-400' :
                        perm.startsWith('update:') ? 'border-amber-500 text-amber-400' :
                        perm.startsWith('delete:') ? 'border-red-500 text-red-400' :
                        perm.startsWith('approve:') ? 'border-purple-500 text-purple-400' :
                        perm.startsWith('manage:') ? 'border-pink-500 text-pink-400' :
                        'border-slate-500 text-slate-400'
                      }`}
                    >
                      {perm}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Organization Dialog */}
      <Dialog open={showCreateOrgDialog} onOpenChange={setShowCreateOrgDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Organization</DialogTitle>
            <DialogDescription>Add a new organization to the hierarchy</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={orgForm.name}
                  onChange={(e) => setOrgForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Organization name"
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Arabic Name</Label>
                <Input
                  value={orgForm.name_ar}
                  onChange={(e) => setOrgForm(prev => ({ ...prev, name_ar: e.target.value }))}
                  placeholder="الاسم بالعربية"
                  className="bg-slate-800 border-slate-700"
                  dir="rtl"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select
                  value={orgForm.org_type}
                  onValueChange={(v) => setOrgForm(prev => ({ ...prev, org_type: v, code: '' }))}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(orgTypeLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Code *</Label>
                {orgTypeCodes[orgForm.org_type] ? (
                  <Select
                    value={orgForm.code}
                    onValueChange={(v) => {
                      const selectedCode = orgTypeCodes[orgForm.org_type]?.find(c => c.code === v);
                      setOrgForm(prev => ({ 
                        ...prev, 
                        code: v,
                        name: selectedCode?.label || prev.name,
                        name_ar: selectedCode?.label_ar || prev.name_ar
                      }));
                    }}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 font-mono">
                      <SelectValue placeholder="Select code..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {/* Group by category if department type */}
                      {orgForm.org_type === 'department' ? (
                        <>
                          {['Cockpit Crew', 'Cabin Crew', 'Operations', 'Maintenance', 'Ground', 'Safety', 'Training'].map(category => {
                            const categoryItems = orgTypeCodes.department.filter(c => c.category === category);
                            if (categoryItems.length === 0) return null;
                            return (
                              <div key={category}>
                                <div className="px-2 py-1.5 text-xs font-semibold text-slate-400 bg-slate-800">{category}</div>
                                {categoryItems.map(item => (
                                  <SelectItem key={item.code} value={item.code}>
                                    <span className="font-mono mr-2">{item.code}</span>
                                    <span className="text-slate-400">- {item.label}</span>
                                  </SelectItem>
                                ))}
                              </div>
                            );
                          })}
                        </>
                      ) : (
                        orgTypeCodes[orgForm.org_type].map(item => (
                          <SelectItem key={item.code} value={item.code}>
                            <span className="font-mono mr-2">{item.code}</span>
                            <span className="text-slate-400">- {item.label}</span>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={orgForm.code}
                    onChange={(e) => setOrgForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                    placeholder="ORG"
                    className="bg-slate-800 border-slate-700 font-mono"
                    maxLength={10}
                  />
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Parent Organization</Label>
              <Select
                value={orgForm.parent_id || 'none'}
                onValueChange={(v) => setOrgForm(prev => ({ ...prev, parent_id: v === 'none' ? null : v }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Select parent..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Parent (Root)</SelectItem>
                  {organizations.map(org => (
                    <SelectItem key={org.id} value={org.id}>
                      {'—'.repeat(org.level)} {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={orgForm.description}
                onChange={(e) => setOrgForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Organization description"
                className="bg-slate-800 border-slate-700"
              />
            </div>
            
            {orgForm.org_type === 'operator' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Regulatory Authority</Label>
                  <Select
                    value={orgForm.regulatory_authority}
                    onValueChange={(v) => setOrgForm(prev => ({ ...prev, regulatory_authority: v }))}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GACA">GACA</SelectItem>
                      <SelectItem value="EASA">EASA</SelectItem>
                      <SelectItem value="FAA">FAA</SelectItem>
                      <SelectItem value="ICAO">ICAO</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>AOC Number</Label>
                  <Input
                    value={orgForm.aoc_number}
                    onChange={(e) => setOrgForm(prev => ({ ...prev, aoc_number: e.target.value }))}
                    placeholder="AOC-XXX-XXX"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateOrgDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateOrg} disabled={!orgForm.name || !orgForm.code}>
              Create Organization
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Organization Dialog */}
      <Dialog open={showEditOrgDialog} onOpenChange={setShowEditOrgDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Organization</DialogTitle>
            <DialogDescription>Update organization details</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={orgForm.name}
                  onChange={(e) => setOrgForm(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Arabic Name</Label>
                <Input
                  value={orgForm.name_ar}
                  onChange={(e) => setOrgForm(prev => ({ ...prev, name_ar: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                  dir="rtl"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={orgForm.org_type}
                  onValueChange={(v) => setOrgForm(prev => ({ ...prev, org_type: v }))}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(orgTypeLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Code</Label>
                {orgTypeCodes[orgForm.org_type] ? (
                  <Select
                    value={orgForm.code}
                    onValueChange={(v) => setOrgForm(prev => ({ ...prev, code: v }))}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 font-mono">
                      <SelectValue placeholder="Select code..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {orgForm.org_type === 'department' ? (
                        <>
                          {['Cockpit Crew', 'Cabin Crew', 'Operations', 'Maintenance', 'Ground', 'Safety', 'Training'].map(category => {
                            const categoryItems = orgTypeCodes.department.filter(c => c.category === category);
                            if (categoryItems.length === 0) return null;
                            return (
                              <div key={category}>
                                <div className="px-2 py-1.5 text-xs font-semibold text-slate-400 bg-slate-800">{category}</div>
                                {categoryItems.map(item => (
                                  <SelectItem key={item.code} value={item.code}>
                                    <span className="font-mono mr-2">{item.code}</span>
                                    <span className="text-slate-400">- {item.label}</span>
                                  </SelectItem>
                                ))}
                              </div>
                            );
                          })}
                        </>
                      ) : (
                        orgTypeCodes[orgForm.org_type].map(item => (
                          <SelectItem key={item.code} value={item.code}>
                            <span className="font-mono mr-2">{item.code}</span>
                            <span className="text-slate-400">- {item.label}</span>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={orgForm.code}
                    onChange={(e) => setOrgForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                    className="bg-slate-800 border-slate-700 font-mono"
                  />
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={orgForm.description}
                onChange={(e) => setOrgForm(prev => ({ ...prev, description: e.target.value }))}
                className="bg-slate-800 border-slate-700"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditOrgDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateOrg}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Members Dialog */}
      <Dialog open={showMembersDialog} onOpenChange={setShowMembersDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-sky-400" />
              Members of {selectedOrg?.name}
            </DialogTitle>
            <DialogDescription>
              View and manage users who have access to this organization
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setShowAddMemberDialog(true)}>
                <UserPlus className="w-4 h-4 mr-2" />
                Add Member
              </Button>
            </div>
            
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800">
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Access</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map(member => (
                  <TableRow key={member.id} className="border-slate-800">
                    <TableCell>
                      <div>
                        <p className="font-medium">{member.user?.name || 'Unknown'}</p>
                        <p className="text-sm text-slate-400">{member.user?.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${roleColors[member.role]} text-white`}>
                        {roleLabels[member.role]}
                      </Badge>
                      {member.is_primary && (
                        <Badge className="ml-2 bg-amber-500/20 text-amber-400">Primary</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {member.can_access_children && (
                          <Badge variant="outline" className="text-xs">+Children</Badge>
                        )}
                        {member.can_access_parent && (
                          <Badge variant="outline" className="text-xs">+Parent</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleRemoveMember(member.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {members.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-slate-400 py-8">
                      No members in this organization
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={showAddMemberDialog} onOpenChange={setShowAddMemberDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle>Add Member to {selectedOrg?.name}</DialogTitle>
            <DialogDescription>
              Assign a user to this organization with a specific role and access level
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>User *</Label>
              <Select
                value={memberForm.user_id}
                onValueChange={(v) => setMemberForm(prev => ({ ...prev, user_id: v }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Select user..." />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Role *</Label>
              <Select
                value={memberForm.role}
                onValueChange={(v) => setMemberForm(prev => ({ ...prev, role: v }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(roleLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={memberForm.is_primary}
                  onChange={(e) => setMemberForm(prev => ({ ...prev, is_primary: e.target.checked }))}
                  className="rounded"
                />
                <span className="text-sm">Primary Organization</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={memberForm.can_access_children}
                  onChange={(e) => setMemberForm(prev => ({ ...prev, can_access_children: e.target.checked }))}
                  className="rounded"
                />
                <span className="text-sm">Access Children</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={memberForm.can_access_parent}
                  onChange={(e) => setMemberForm(prev => ({ ...prev, can_access_parent: e.target.checked }))}
                  className="rounded"
                />
                <span className="text-sm">Access Parent</span>
              </label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMemberDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddMember} disabled={!memberForm.user_id}>
              Add Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrganizationManagementPage;
